/* eslint-disable no-console */

import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
} from "react";
import { toast } from "react-toastify";

import authApi from "services/authApi";
import dealerCareApi from "services/dealerCareApi";
import { configureAuthInterceptors } from "services/configureAuthInterceptors";
import {
  isRefreshTokenExpired,
  persistAuthTokens,
  updateApiAuthorizationHeader,
} from "context/auth/tokenStorage";
import Axios from "axios";
import { clarityManager } from "libs/clarity/clarity";

interface SignInCredentials {
  username: string;
  password: string;
}

export interface Terms {
  acceptedAt: string | null;
  agreement: {
    acceptUntil: string;
    disableAt: string;
    effectiveAt: string;
    id: string;
    version: string;
  };
  disabledAt: string | null;
  id: string;
}

interface AuthContextProps {
  token: string;
  refreshToken: string;
  userId: string;
  dealerId: string;
  profileData: UserProfileResponse["data"];
  isAuthLoading: boolean;
  needCreateNewPassword: boolean;
  recoverPassword(userName: string): Promise<void>;
  signIn(credentials: SignInCredentials): Promise<void>;
  signOut(): void;
  cancelNewPasswordCreation(): void;
  updateTerms(terms: Terms): void;
  dealerName: string;
  terms: Terms | null;
}

interface DataState {
  token: string;
  refreshToken: string;
  userId: string;
  dealerId: string;
  profileData: UserProfileResponse["data"];
  tokenExpiration: number;
  refreshTokenExpiration: number;
  needCreateNewPassword: boolean;
  dealerName: string;
  terms: Terms;
}

interface SignInResponse {
  accessToken: string;
  refreshToken: string;
  expiresAccessTokenIn: number;
  expiresRefreshTokenIn: number;
  id: string;
}

interface UserResponse {
  data: {
    dealerId: string;
    email: string;
    name: string;
    imageUrl: string;
    profile: string;
    cellphone: {
      number: string;
      areaCode: string;
      countryCode: string;
    };
    id: string;
  };
}

interface UserProfileResponse {
  data: {
    name: string;
    userName: string;
    email: string;
    passwordChangePending: boolean;
    emailConfirmed: boolean;
    phoneNumber: string;
    phoneNumberConfirmed: boolean;
    twoFactorEnabled: boolean;
    userType: string;
    dealerId: string;
    imageUrl: string;
    cellphone: string;
    profile: string;
    id: string;
  };
}

interface ApiErrorPayload {
  code?: string;
  errors?: { code?: string }[];
}

function resolveApiErrorCode(error: unknown): string | undefined {
  if (!Axios.isAxiosError(error)) return undefined;

  const payload = error.response?.data as ApiErrorPayload | undefined;
  if (!payload) return undefined;

  if (payload.code) return payload.code;

  return payload.errors?.[0]?.code;
}

const INVALID_PASSWORD_ERROR_CODE = "User.InvalidPassword";
const USER_NOT_FOUND_ERROR_CODE = "User.NotFound";
const INVALID_CREDENTIALS_MESSAGE =
  "E-mail ou senha inválidos, tente novamente.";
const INVALID_RECOVER_PASSWORD_EMAIL_MESSAGE =
  "E-mail inválido, valide a informação e tente novamente";
const ERROR_TOAST_OPTIONS = { type: "error" as const };

const AuthContext = createContext<AuthContextProps>({} as AuthContextProps);

const AuthProvider = ({ children }: { children: JSX.Element }) => {
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [data, setData] = useState<DataState>(() => {
    const token = localStorage.getItem("@ctech:token");
    const refreshToken = localStorage.getItem("@ctech:refreshToken");
    const tokenExpiration = Number(
      localStorage.getItem("@ctech:tokenExpiration"),
    );
    const refreshTokenExpiration = Number(
      localStorage.getItem("@ctech:refreshTokenExpiration"),
    );
    const userId = localStorage.getItem("@ctech:userId");
    const dealerId = localStorage.getItem("@ctech:dealerId");
    const profileData = localStorage.getItem("@ctech:profileData");
    const dealerName = localStorage.getItem("@ctech:dealerName");
    const dealerInfo = localStorage.getItem("@ctech:dealerInfo");
    const terms = JSON.parse(dealerInfo || "null")?.termsOfService;

    if (isRefreshTokenExpired()) {
      localStorage.clear();
      return {} as DataState;
    }

    if (
      token &&
      refreshToken &&
      userId &&
      dealerId &&
      tokenExpiration &&
      refreshTokenExpiration &&
      profileData &&
      dealerName &&
      dealerInfo
    ) {
      updateApiAuthorizationHeader(token);

      return {
        token,
        refreshToken,
        tokenExpiration,
        refreshTokenExpiration,
        dealerId,
        userId,
        needCreateNewPassword: false,
        profileData: JSON.parse(profileData),
        dealerName,
        terms,
      };
    }

    localStorage.clear();

    return {} as DataState;
  });

  const signOut = useCallback(() => {
    localStorage.clear();
    setData({} as DataState);
  }, []);

  const refreshAuthToken = useCallback(async (): Promise<string | null> => {
    const refreshToken = localStorage.getItem("@ctech:refreshToken") || "";

    if (!refreshToken || isRefreshTokenExpired()) {
      return null;
    }

    try {
      const { data: loginData } = await authApi.post<SignInResponse>(
        "v1/users/login",
        {
          refreshToken,
          scope: "dealer",
          grantType: "refreshToken",
        },
      );

      const {
        accessToken: token,
        id: userId,
        refreshToken: newRefreshToken,
        expiresAccessTokenIn: tokenExpiration,
        expiresRefreshTokenIn: refreshTokenExpiration,
      } = loginData;

      persistAuthTokens({
        token,
        refreshToken: newRefreshToken,
        tokenExpiration,
        refreshTokenExpiration,
        userId,
      });

      setData((prevState) => ({
        ...prevState,
        token,
        userId,
        refreshToken: newRefreshToken,
        tokenExpiration,
        refreshTokenExpiration,
      }));

      return token;
    } catch {
      return null;
    }
  }, []);

  const recoverPassword = useCallback(async (email: string) => {
    setIsAuthLoading(true);
    try {
      await authApi.put(`/v1/users/password/temporary`, {
        email,
        scope: "Dealer",
      });
      toast("Enviamos as instruções de recuperação ao email informado.", {
        type: "success",
      });
    } catch (err: unknown) {
      const errorCode = resolveApiErrorCode(err);

      if (errorCode === USER_NOT_FOUND_ERROR_CODE) {
        toast(INVALID_RECOVER_PASSWORD_EMAIL_MESSAGE, {
          ...ERROR_TOAST_OPTIONS,
        });
      } else {
        toast("Ops, algo deu errado. Tente novamente.", {
          ...ERROR_TOAST_OPTIONS,
        });
      }
    } finally {
      setIsAuthLoading(false);
    }
  }, []);

  const updateTerms = useCallback((terms: Terms) => {
    setData((oldData) => ({ ...oldData, terms }));
  }, []);

  const signIn = useCallback(
    async ({ username, password }: SignInCredentials) => {
      try {
        setIsAuthLoading(true);
        const { data: loginData } = await authApi.post<SignInResponse>(
          "v1/users/login",
          {
            username,
            password,
            scope: "dealer",
            grantType: "password",
          },
        );

        if (loginData.id) {
          clarityManager.identify(loginData.id);
        }

        const {
          accessToken: token,
          id: userId,
          refreshToken,
          expiresAccessTokenIn: tokenExpiration,
          expiresRefreshTokenIn: refreshTokenExpiration,
        } = loginData;

        persistAuthTokens({
          token,
          refreshToken,
          tokenExpiration,
          refreshTokenExpiration,
          userId,
        });

        const [{ data: userData }, { data: userProfileData }] =
          await Promise.all([
            dealerCareApi.get<UserResponse>(`v1/dealers/users/${loginData.id}`),
            dealerCareApi.get<UserProfileResponse>(
              `v1/dealers/users/${loginData.id}/profile`,
            ),
          ]);

        const dealerData = await dealerCareApi.get<{
          data: { tradeName: string; termsOfService: Terms };
        }>(`v1/dealers/${userProfileData.data.dealerId}`);

        if (userProfileData.data.passwordChangePending) {
          setData({
            needCreateNewPassword: true,
          } as DataState);
          return;
        }

        localStorage.setItem("@ctech:dealerId", userData.data.dealerId);
        localStorage.setItem(
          "@ctech:profileData",
          JSON.stringify(userProfileData.data),
        );
        localStorage.setItem(
          "@ctech:dealerName",
          dealerData.data.data.tradeName.toString(),
        );
        localStorage.setItem(
          "@ctech:dealerInfo",
          JSON.stringify(dealerData.data.data),
        );

        setData({
          token,
          userId,
          dealerId: userData.data.dealerId,
          profileData: userProfileData.data,
          refreshToken,
          tokenExpiration,
          refreshTokenExpiration,
          needCreateNewPassword: userProfileData.data.passwordChangePending,
          dealerName: dealerData.data.data.tradeName,
          terms: dealerData.data.data.termsOfService,
        });
      } catch (err: unknown) {
        const errorCode = resolveApiErrorCode(err);

        if (
          errorCode === INVALID_PASSWORD_ERROR_CODE ||
          errorCode === USER_NOT_FOUND_ERROR_CODE
        ) {
          toast(INVALID_CREDENTIALS_MESSAGE, ERROR_TOAST_OPTIONS);
        } else {
          toast("Ops, algo deu errado. Tente novamente.", ERROR_TOAST_OPTIONS);
        }
      } finally {
        setIsAuthLoading(false);
      }
    },
    [],
  );

  function cancelNewPasswordCreation() {
    setData({} as DataState);
  }

  useEffect(() => {
    return configureAuthInterceptors(refreshAuthToken, signOut);
  }, [refreshAuthToken, signOut]);

  return (
    <AuthContext.Provider
      value={{
        ...data,
        isAuthLoading,
        signIn,
        signOut,
        cancelNewPasswordCreation,
        recoverPassword,
        updateTerms,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextProps {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within a AuthProvider");
  }
  return context;
}

export { useAuth, AuthProvider };
